import dayjs from 'dayjs';

import {
  adsSources,
  invalidVisionUgcSources,
  sourceTypeEnum,
  visionPredictedSources,
  visualSources,
} from '@/app/library/constants';
import { PREDICTION_MODEL } from '@/config';
import { useAuthStore } from '@/stores/auth';

// Routes where Vision-AI prediction badges should be suppressed regardless of
// brand/source. Currently empty — the legacy `'instagram.galleries'` entry was
// introduced in 2021 (#4469) to hide predictions on a Boards index layout that
// rendered media cards inline. That layout is gone: the index now renders
// board thumbnails (DashGalleryCard) which don't consult showPredictions, and
// individual media only appears via the Select Media drawer opened on top of
// the same route. The blanket exclusion was suppressing predictions in that
// drawer for every channel-typed Boards page (sc-197870 follow-up).
export const EXCLUDED_ROUTES = [];

export const engagementRating = {
  good: 0.5,
  excellent: 0.75,
};

export const day = {
  today: 'Today',
  yesterday: 'Yesterday',
  lastSevenDays: 'Last 7 Days',
  last30Days: 'Last 30 Days',
};

export function getRating(value) {
  if (value === null || value === undefined) {
    return null;
  }
  if (value > engagementRating.excellent) {
    return 'excellent';
  }
  if (value > engagementRating.good) {
    return 'good';
  }
  return 'bad';
}

export function getPredictionRating(
  predictions,
  { currentModel = PREDICTION_MODEL.ENGAGEMENT, mediaV2 = false } = {},
) {
  if (!predictions || !currentModel) return null;
  const field = mediaV2 ? currentModel.v2field : currentModel.field;
  if (predictions === 'processing' || predictions[field] === 'processing') {
    return 'processing';
  }
  return getRating(predictions[field]);
}

const PREDICTION_TIER_RANGES = Object.freeze({
  top: { from: engagementRating.excellent, to: 1 },
  high: { from: engagementRating.good, to: engagementRating.excellent },
  low: { from: 0, to: engagementRating.good },
});

// Legacy single-string value from the four-option radio era. Expanded into the
// underlying tiers so old URLs/saved filters keep working.
function expandLegacyTier(tier) {
  return tier === 'top_high' ? ['top', 'high'] : [tier];
}

export function getPerformanceParameters(value) {
  // Accepts either a single tier string (legacy single-select callers and
  // legacy `'top_high'` URL values) or an array of tier strings (multi-select
  // Predicted Performance filter, sc-197870). Returns the union `{from, to}`
  // range covering all selected tiers. An empty/null/undefined value returns
  // `{from: null, to: null}` so callers can detect "no filter".
  if (value === undefined || value === null) {
    return { from: null, to: null };
  }
  const tiers = (Array.isArray(value) ? value : [value])
    .flatMap(expandLegacyTier)
    .filter((tier) => tier in PREDICTION_TIER_RANGES);
  if (tiers.length === 0) {
    return { from: null, to: null };
  }
  const ranges = tiers.map((tier) => PREDICTION_TIER_RANGES[tier]);
  return {
    from: Math.min(...ranges.map((r) => r.from)),
    to: Math.max(...ranges.map((r) => r.to)),
  };
}

export function showPredictions(media, router) {
  const authStore = useAuthStore();
  // Check existence of required properties
  if (!media) return false;
  let mediaSource = media.sourceType?.split(':');
  mediaSource = mediaSource ? mediaSource[mediaSource.length - 1] : media.source;
  if (!mediaSource) return false;

  // Check base permissions
  if (!authStore.user_can('vision', 'can_access_vision')) return false;

  // Source exclusions
  const isVisionPredictedSource =
    visionPredictedSources.includes(mediaSource) ||
    (mediaSource === sourceTypeEnum.UGC && !invalidVisionUgcSources.includes(media.source));
  if (!isVisionPredictedSource && !Object.values(visualSources).includes(mediaSource)) {
    return false;
  }
  if (Object.values(adsSources).includes(mediaSource)) return false;

  // Route exclusions
  const routeName = router?.currentRoute?.value?.name;
  if (routeName && EXCLUDED_ROUTES.find((r) => routeName.startsWith(r))) {
    return false;
  }
  // Normal users show predictions on vision prediction sources
  return isVisionPredictedSource;
}

export function formatDate(date) {
  const formatType = 'dddd, MMMM Do';
  const currDate = dayjs().format(formatType);
  const yesterdaysDate = dayjs().subtract(1, 'day').format(formatType);
  let formattedDate = dayjs(new Date(date)).format(formatType);
  if (formattedDate === currDate) {
    formattedDate = day.today;
  } else if (formattedDate === yesterdaysDate) {
    formattedDate = day.yesterday;
  }
  return formattedDate;
}

export function formatTimestamp(timeCreated) {
  return dayjs(new Date(timeCreated)).format('h:mm a');
}
